import type {
  CreateStoreCameraInput,
  JvcCamera,
  SetStoreCameraTypeInput,
  StoreCamera,
  StoreCameraConfigUpsertInput,
  UpdateCameraStreamInput,
  UpdateStoreCameraInput,
} from '@gozoki/api-types'
import { mutationOptions } from '@tanstack/react-query'
import { gozokiApiWithAuth } from '../gozokiApi'
import { fetchStoreDvrCameras } from '../queries/stores.queries'
import { keys } from '../queries/keys'

export const upsertStoreCameraConfig = async ({
  storeId,
  ...data
}: StoreCameraConfigUpsertInput) => {
  await gozokiApiWithAuth.put(`/stores/${storeId}/camera-config`, data)
}

export const deleteStoreCameraConfig = async ({ storeId }: { storeId: number }) => {
  await gozokiApiWithAuth.delete(`/stores/${storeId}/camera-config`)
}

export const createStoreCamera = async ({
  storeId,
  ...data
}: CreateStoreCameraInput): Promise<StoreCamera> => {
  const response = await gozokiApiWithAuth.post<StoreCamera>(`/stores/${storeId}/cameras`, data)
  return response.data
}

export const updateStoreCamera = async ({
  storeId,
  cameraId,
  ...data
}: UpdateStoreCameraInput): Promise<StoreCamera> => {
  const response = await gozokiApiWithAuth.put<StoreCamera>(
    `/stores/${storeId}/cameras/${cameraId}`,
    data,
  )
  return response.data
}

export const deleteStoreCamera = async ({
  storeId,
  cameraId,
}: {
  storeId: number
  cameraId: number
}) => {
  await gozokiApiWithAuth.delete(`/stores/${storeId}/cameras/${cameraId}`)
}

export const upsertStoreCameraConfigOptions = () =>
  mutationOptions({
    mutationFn: upsertStoreCameraConfig,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.stores.cameraConfig.summary(variables.storeId),
      })
    },
  })

export const deleteStoreCameraConfigOptions = () =>
  mutationOptions({
    mutationFn: deleteStoreCameraConfig,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.stores.cameraConfig.summary(variables.storeId),
      })
    },
  })

export const createStoreCameraOptions = () =>
  mutationOptions({
    mutationFn: createStoreCamera,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.stores.cameraConfig.cameras.list(variables.storeId),
      })
    },
  })

export const updateStoreCameraOptions = () =>
  mutationOptions({
    mutationFn: updateStoreCamera,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.stores.cameraConfig.cameras.list(variables.storeId),
      })
      context.client.invalidateQueries({
        queryKey: keys.stores.cameraConfig.cameras.byId(variables.storeId, variables.cameraId),
      })
    },
  })

export const deleteStoreCameraOptions = () =>
  mutationOptions({
    mutationFn: deleteStoreCamera,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.stores.cameraConfig.cameras.list(variables.storeId),
      })
      context.client.removeQueries({
        queryKey: keys.stores.cameraConfig.cameras.byId(variables.storeId, variables.cameraId),
      })
    },
  })

const updateCameraStreamNew = async ({ storeId, ...data }: UpdateCameraStreamInput) => {
  await gozokiApiWithAuth.put(`/stores/${storeId}/camera-streams`, data)
}

export const setStoreCameraType = async ({ storeId, type }: SetStoreCameraTypeInput) => {
  await gozokiApiWithAuth.patch(`/stores/${storeId}/camera-type`, { type })
}

export const updateCameraStreamOptions = () =>
  mutationOptions({
    mutationFn: updateCameraStreamNew,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.stores.cameraStream(variables.storeId),
      })
      context.client.invalidateQueries({
        queryKey: keys.stores.byId(variables.storeId),
      })
      // Saving a JVC config always reconciles the store's camera worker, so the
      // runtime summary is stale afterwards.
      context.client.invalidateQueries({
        queryKey: keys.stores.cameraConfig.summary(variables.storeId),
      })
    },
  })

export const setStoreCameraTypeOptions = () =>
  mutationOptions({
    mutationFn: setStoreCameraType,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.stores.byId(variables.storeId),
      })
    },
  })

export const setStoreCpiInventoryEnabled = async ({
  storeId,
  enabled,
}: {
  storeId: number
  enabled: boolean
}) => {
  await gozokiApiWithAuth.patch(`/stores/${storeId}/cpi-inventory-enabled`, { enabled })
}

export const setStoreCpiInventoryEnabledOptions = () =>
  mutationOptions({
    mutationFn: setStoreCpiInventoryEnabled,
    onSuccess: (_data, variables, _onResult, context) => {
      context.client.invalidateQueries({
        queryKey: keys.stores.byId(variables.storeId),
      })
    },
  })

// Fetch-only : ne persiste pas. Le BO appelle ensuite updateCameraStream pour sauver.
export const fetchStoreDvrCamerasOptions = () =>
  mutationOptions<JvcCamera[], Error, number>({
    mutationFn: (storeId) => fetchStoreDvrCameras(storeId),
  })
